/**
 * Phase 18: SwiftData's stores in the browser's IndexedDB.
 *
 * The app keeps its models in memory (Sources/SwiftData) and the page keeps
 * them across launches: before the module starts, every record of the app's
 * database is read and handed in as `SB_SWIFTDATA`
 * (`{"<store>": [{id, entity, values}, …]}`), and each `swiftdata` op (one
 * save) is written back in one transaction. One database per app,
 * `sb-swiftdata:<App>`, with one object store, `records`, keyed by
 * `[store, id]`. See docs/ops-protocol.md, "Phase 18".
 */

import type { SwiftDataOp, SwiftDataRecord } from './protocol';

/** Every store of an app: store name → its records, in key order. */
export type SwiftDataStores = Record<string, SwiftDataRecord[]>;

/** What is kept in the `records` object store. */
interface StoredRecord extends SwiftDataRecord {
  store: string;
}

const RECORDS = 'records';

export function databaseName(app: string): string {
  return `sb-swiftdata:${app}`;
}

/** `stores` with one save applied, as the database applies it (for tests and the headless runner). */
export function applySwiftDataOp(stores: SwiftDataStores, op: SwiftDataOp): SwiftDataStores {
  const records = new Map<number, SwiftDataRecord>();
  if (!op.clear) for (const record of stores[op.store] ?? []) records.set(record.id, record);
  for (const id of op.delete) records.delete(id);
  for (const record of op.put) records.set(record.id, { id: record.id, entity: record.entity, values: record.values });
  return { ...stores, [op.store]: [...records.values()].sort((a, b) => a.id - b.id) };
}

/** The stores as the module reads them from `SB_SWIFTDATA`; null when there is nothing to hand in. */
export function launchValue(stores: SwiftDataStores): string | null {
  const nonEmpty = Object.entries(stores).filter(([, records]) => records.length > 0);
  if (nonEmpty.length === 0) return null;
  return JSON.stringify(Object.fromEntries(nonEmpty));
}

/** Groups stored records by store, each in key order. */
export function groupRecords(rows: readonly StoredRecord[]): SwiftDataStores {
  const stores: SwiftDataStores = {};
  for (const row of rows) {
    (stores[row.store] ??= []).push({ id: row.id, entity: row.entity, values: row.values });
  }
  for (const records of Object.values(stores)) records.sort((a, b) => a.id - b.id);
  return stores;
}

/** Where an app's SwiftData lives between launches. */
export interface SwiftDataPersistence {
  /** Every store, read once before the module starts. */
  load(): Promise<SwiftDataStores>;
  /** Writes one save; saves are written in the order they were made. */
  apply(op: SwiftDataOp): Promise<void>;
  /** Deletes the app's data (every store). */
  clear(): Promise<void>;
}

function request<T>(req: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error ?? new Error('IndexedDB request failed'));
  });
}

function done(transaction: IDBTransaction): Promise<void> {
  return new Promise((resolve, reject) => {
    transaction.oncomplete = () => resolve();
    transaction.onabort = () => reject(transaction.error ?? new Error('IndexedDB transaction aborted'));
    transaction.onerror = () => reject(transaction.error ?? new Error('IndexedDB transaction failed'));
  });
}

/** The app's database in IndexedDB (`factory` is the browser's unless a test passes one). */
export function indexedDBPersistence(app: string, factory: IDBFactory = indexedDB): SwiftDataPersistence {
  let opened: Promise<IDBDatabase> | null = null;
  const database = (): Promise<IDBDatabase> => {
    opened ??= new Promise((resolve, reject) => {
      const open = factory.open(databaseName(app), 1);
      open.onupgradeneeded = () => {
        if (!open.result.objectStoreNames.contains(RECORDS)) open.result.createObjectStore(RECORDS, { keyPath: ['store', 'id'] });
      };
      open.onsuccess = () => resolve(open.result);
      open.onerror = () => reject(open.error ?? new Error(`could not open ${databaseName(app)}`));
      open.onblocked = () => reject(new Error(`${databaseName(app)} is blocked by another tab`));
    });
    return opened;
  };
  // Saves are chained so that each one's transaction starts after the
  // previous one's was created, whatever the database's opening takes.
  let queue: Promise<void> = Promise.resolve();

  return {
    async load() {
      const db = await database();
      const transaction = db.transaction(RECORDS, 'readonly');
      const rows = await request(transaction.objectStore(RECORDS).getAll() as IDBRequest<StoredRecord[]>);
      return groupRecords(rows);
    },
    apply(op) {
      queue = queue.then(async () => {
        const db = await database();
        const transaction = db.transaction(RECORDS, 'readwrite');
        const records = transaction.objectStore(RECORDS);
        if (op.clear) records.delete(IDBKeyRange.bound([op.store, -Infinity], [op.store, Infinity]));
        for (const id of op.delete) records.delete([op.store, id]);
        for (const record of op.put) {
          const row: StoredRecord = { store: op.store, id: record.id, entity: record.entity, values: record.values };
          records.put(row);
        }
        await done(transaction);
      });
      return queue;
    },
    async clear() {
      const db = await database();
      const transaction = db.transaction(RECORDS, 'readwrite');
      transaction.objectStore(RECORDS).clear();
      await done(transaction);
    },
  };
}
